import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { config } from '../../config/environment';
import { CognitoChallenge } from '../../services/cognitoAuthService';
import { landingPathForRole } from '../../utils/landingPath';

interface LoginPageProps {
  // feat-191 (C2): `role` is the management role AuthContext resolved during this
  // login. Optional — an older caller, a login that returned no management row, or
  // a test stub simply lands on the unchanged default.
  onLogin: (email: string, password: string) => Promise<{ success: boolean; challenge?: CognitoChallenge; role?: string }>;
  onPasswordChangeRequired?: (email: string, challenge: CognitoChallenge) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, onPasswordChangeRequired }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // feat-163: AC3 requires "success → redirect to /scorecard-login WITH a
  // success message", and this component's only message surface was `error`.
  // ResetPasswordPage carries the copy here in router state. Read once on
  // render: it is display-only and never influences auth.
  const successMessage = (location.state as { message?: string } | null)
    ?.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await onLogin(email, password);
      
      if (result.success) {
        // feat-191 C2, redirect site 2 of 4. Staff keep the pilot dashboard
        // (feat-083); an evaluator goes to the interaction-evaluations page,
        // which is the only surface they can pass a route guard on.
        navigate(landingPathForRole(result.role));
      } else if (result.challenge) {
        // Password change required
        if (onPasswordChangeRequired) {
          onPasswordChangeRequired(email, result.challenge);
        } else {
          // Fallback: navigate to password change page with state
          navigate('/change-password', { 
            state: { 
              email, 
              session: result.challenge.session 
            } 
          });
        }
      } else {
        setError('Invalid email or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      // Show the actual error message from the backend
      const errorMessage = error instanceof Error ? error.message : 'An error occurred during login. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bespoke AI</h1>
          <h2 className="text-xl text-gray-600 mb-8">Restaurant Management System</h2>
        </div>
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              <p className="text-sm">{successMessage}</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="mt-2 flex items-center">


              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Forgot password?
              </button>
            </div>
          </form>

          {config.useDummyData && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-600 text-center">
                <strong>Demo Credentials (Dev Environment):</strong><br />
                Email: <code className="bg-gray-200 px-1 rounded">admin@bespoke.ai</code><br />
                Password: <code className="bg-gray-200 px-1 rounded">password123</code>
              </p>
            </div>
          )}
          
          {!config.useDummyData && config.environment === 'dev' && (
            <div className="mt-6 p-4 bg-yellow-50 rounded-md">
              <p className="text-xs text-yellow-600 text-center">
                <strong>Environment: dev | Auth: Cognito</strong><br />
                Use your AWS Cognito credentials to sign in.
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default LoginPage;
